import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { listOptions, useConfig } from '../context/ConfigContext';

// TS port of the source (Falah) app's pages/CustomerInvoiceForm.jsx.
//
// Dropped/renamed vs. source: the source's `donor_id` field posted against
// `/donors` — VNPF's backend has no Donor model. CustomerInvoiceController
// validates `member_id` (nullable, exists:members,id) instead — this is the
// same self-service linkage concept (Donor was renamed Member in this
// migration), so the field is kept as an optional Member picker, just
// pointed at `/members` and `member_id`. See api/app/Http/Controllers/
// CustomerInvoiceController.php::validated() and api/app/Models/Member.php
// (identity fields live on the linked User, not Member itself — hence
// `m.user?.first_name`/`last_name` below rather than `m.first_name`).
//
// The members fetch is best-effort/tolerant of a 403: a Revenue
// Administrator does not necessarily hold memberships.view or
// member.profile.view (see routes/api.php's `permission:memberships.view|
// member.profile.view` gate on GET /members), so a Revenue-only admin
// would otherwise be unable to load this form at all just because the
// optional member picker's own request failed.
// Dropped vs. source: the source branched this label (and a category list,
// and whether the Member picker showed at all) on type === 'donation'/
// 'zakat' — both mosque-specific concepts with no VNPF equivalent, and
// CustomerInvoiceController no longer gives `category` any special
// per-type validation (see its validated() method — it's now just a plain
// optional string). Category is a free-text field and the Member picker is
// always available below, regardless of invoice type.
const NAME_LABELS: Record<string, string> = {
  general: 'Customer name',
};

interface InvoiceFormState {
  type: string;
  category: string;
  member_id: string | number;
  customer_quotation_id: string | number;
  customer_name: string;
  amount: string;
  invoice_date: string;
  due_date: string;
  notes: string;
}

const emptyForm: InvoiceFormState = {
  type: '',
  category: '',
  member_id: '',
  customer_quotation_id: '',
  customer_name: '',
  amount: '',
  invoice_date: new Date().toISOString().slice(0, 10),
  due_date: '',
  notes: '',
};

interface MemberOption {
  id: number;
  user: { first_name: string; last_name: string } | null;
}

interface QuotationOption {
  id: number;
  quotation_number: string;
  customer_name: string;
  amount: number;
}

type FormErrors = Record<string, string[]>;

export function CustomerInvoiceForm() {
  const { lists, settings } = useConfig();
  const navigate = useNavigate();
  const [form, setForm] = useState<InvoiceFormState>(emptyForm);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [quotations, setQuotations] = useState<QuotationOption[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get<{ data: MemberOption[] }>('/members', { params: { per_page: 100 } })
      .then((res) => setMembers(res.data.data))
      .catch(() => setMembers([]));
    api
      .get<{ data: QuotationOption[] }>('/customer-quotations', { params: { available_for_invoice: 1, per_page: 100 } })
      .then((res) => setQuotations(res.data.data));
  }, []);

  const set =
    (key: keyof InvoiceFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const setType = (e: ChangeEvent<HTMLSelectElement>) =>
    setForm({ ...form, type: e.target.value, category: '', member_id: '' });

  const setQuotation = (e: ChangeEvent<HTMLSelectElement>) => {
    const quotationId = e.target.value;
    const quotation = quotations.find((q) => String(q.id) === quotationId);
    setForm({
      ...form,
      customer_quotation_id: quotationId,
      customer_name: quotation ? quotation.customer_name : form.customer_name,
      amount: quotation ? String(quotation.amount) : form.amount,
    });
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    const payload = {
      ...form,
      category: form.category || null,
      member_id: form.member_id || null,
      customer_quotation_id: form.customer_quotation_id || null,
    };
    try {
      const res = await api.post<{ id: number }>('/customer-invoices', payload);
      navigate(`/revenue/invoices/${res.data.id}`);
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Add invoice</h1>
      </header>

      <div className="card">
        <form onSubmit={save} className="form-grid">
          {errors.general && <div className="alert alert-error">{errors.general[0]}</div>}
          <label>
            Type *
            <select value={form.type} onChange={setType} required>
              <option value="">Select…</option>
              {listOptions(lists, 'invoice_type').map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            {errors.type && <small className="error">{errors.type[0]}</small>}
          </label>
          <label>
            Category (optional)
            <input value={form.category} onChange={set('category')} />
            {errors.category && <small className="error">{errors.category[0]}</small>}
          </label>
          <label className="span-2">
            Member (optional)
            <select value={form.member_id} onChange={set('member_id')}>
              <option value="">— Not a member —</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.user ? `${m.user.first_name} ${m.user.last_name}` : `Member #${m.id}`}
                </option>
              ))}
            </select>
          </label>
          <label className="span-2">
            Quotation (optional)
            <select value={form.customer_quotation_id} onChange={setQuotation}>
              <option value="">— No quotation —</option>
              {quotations.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.quotation_number} — {q.customer_name}
                </option>
              ))}
            </select>
          </label>
          <label className="span-2">
            {NAME_LABELS[form.type] ?? 'Customer name'} *
            <input value={form.customer_name} onChange={set('customer_name')} required />
            {errors.customer_name && <small className="error">{errors.customer_name[0]}</small>}
          </label>
          <label>
            Amount ({settings.currency}) *
            <input type="number" step="0.01" min="0.01" value={form.amount} onChange={set('amount')} required />
            {errors.amount && <small className="error">{errors.amount[0]}</small>}
          </label>
          <label>
            Invoice date *
            <input type="date" value={form.invoice_date} onChange={set('invoice_date')} required />
          </label>
          <label>
            Due date
            <input type="date" value={form.due_date} onChange={set('due_date')} />
          </label>
          <label className="span-2">
            Notes
            <textarea rows={2} value={form.notes} onChange={set('notes')} />
          </label>
          <div className="form-actions span-2">
            <Link className="btn btn-ghost" to="/revenue/invoices">
              Cancel
            </Link>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
